import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * Support-chat glyphs exported from the Noor.Merchant Figma support screen
 * (node 378:7231), cleaned to `currentColor` so they inherit color from their
 * container. Use like a lucide icon: `<ChatReadIcon className="size-5" />`.
 *
 * These are the design's own marks — the double-check read receipt, the tilted
 * paper-plane send button and the diagonal attachment link — which differ
 * enough from the lucide equivalents that substituting them reads as wrong.
 */
type IconProps = React.ComponentProps<'svg'>;

/**
 * Thread-header read receipt — a double check mark ("прочитано"). The source
 * mark is a 20px grid; the strokes are drawn on a 24px viewBox here so it sizes
 * consistently with the rest of the kit.
 */
function ChatReadIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M8 12L13 17"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 7L12.5 11.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 17L3 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 9L13 17L7 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Composer attachment affordance — a diagonal chain link. Drawn as strokes on
 * the 24px grid, matching the source's 1.5px weight.
 */
function ChatAttachIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M17.445 12.778L19.778 10.445C21.496 8.727 21.496 5.941 19.778 4.222C18.06 2.504 15.274 2.504 13.555 4.222L11.222 6.555"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.89 15.11L15.11 8.89"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.555 11.222L4.222 13.555C2.504 15.273 2.504 18.059 4.222 19.778C5.94 21.496 8.726 21.496 10.445 19.778L12.778 17.445"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Composer send button — a tilted paper plane (solid). The source art is on an
 * 18.885px square grid; kept verbatim so the silhouette is exact.
 */
function ChatSendIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 18.8852 18.8791"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M13.5825 0.396566L4.5525 3.39657C-1.5175 5.42657 -1.5175 8.73657 4.5525 10.7566L7.2325 11.6466L8.1225 14.3266C10.1425 20.3966 13.4625 20.3966 15.4825 14.3266L18.4925 5.30657C19.8325 1.25657 17.6325 -0.953434 13.5825 0.396566V0.396566ZM13.9025 5.77657L10.1025 9.59657C9.9525 9.74657 9.7625 9.81657 9.5725 9.81657C9.3825 9.81657 9.1925 9.74657 9.0425 9.59657C8.90302 9.45543 8.8248 9.265 8.8248 9.06657C8.8248 8.86814 8.90302 8.6777 9.0425 8.53657L12.8425 4.71657C13.1325 4.42657 13.6125 4.42657 13.9025 4.71657C14.1925 5.00657 14.1925 5.48657 13.9025 5.77657Z"
        fill="currentColor"
      />
    </svg>
  );
}

export { ChatReadIcon, ChatAttachIcon, ChatSendIcon };
export type { IconProps as ChatIconProps };
